<template>
  <div class="mobile-header">
    <!-- Lottery Information -->
    <div class="mobile-lottery-info">
      <div class="mobile-flag-container">
        <img 
          v-if="lottery && hasGameFlag(lottery.game_logo, lottery.game_slug)" 
          :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)" 
          :alt="`${lottery.country} ${$t('forms.flag')}`" 
          class="mobile-flag" 
        />
      </div>
      <div class="mobile-lottery-details">
        <div class="mobile-lottery-title-row">
          <h1 class="mobile-lottery-name">{{ lottery?.game_name || $t('common.loading') }}</h1>
          <button
            v-if="showWatchLive"
            type="button"
            class="watch-live-btn"
            @click="$emit('watch-live')"
          >
            <span class="watch-live-icon" aria-hidden="true">▶</span>
            {{ $t('detail.watchLive') }}
          </button>
        </div>
        <div class="mobile-lottery-country">{{ lottery?.country || '' }}</div>
      </div>
    </div>

    <!-- Countdown Timer -->
    <div class="mobile-countdown-section">
      <div class="mobile-countdown">
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.hours).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.hours') }}</div>
        </div>
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.minutes).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.mins') }}</div>
        </div>
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.seconds).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.secs') }}</div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue';
import { getGameLogoUrl, hasGameFlag } from '../../utils/resultParser';

interface Lottery {
  game_name?: string;
  country?: string;
  game_slug?: string;
  game_logo?: string;
  draw_date?: string;
  cutoff?: string;
}

interface Props {
  lottery: Lottery | null;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  showWatchLive?: boolean;
}

defineProps<Props>();

defineEmits<{
  'go-back': [];
  'watch-live': [];
}>();

</script>

<style scoped>
.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  min-height: 60px;
}

.mobile-lottery-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.mobile-flag-container {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-color);
}

.mobile-flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-lottery-details {
  flex: 1;
  min-width: 0;
}

.mobile-lottery-title-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.mobile-lottery-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.watch-live-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: transparent;
  color: var(--accent-color);
  border: 1px solid var(--accent-color);
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.68rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.watch-live-icon {
  font-size: 0.65rem;
  color: #22c55e;
  line-height: 1;
}

.mobile-lottery-country {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.2;
}

.mobile-countdown-section {
  display: flex;
  align-items: center;
  margin: 0 8px;
  flex-shrink: 0;
  background: var(--bg-dark-card);
  padding: 8px 12px;
  border-radius: 8px;
}

.mobile-countdown {
  display: flex;
  gap: 6px;
  align-items: center;
}

.mobile-time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 28px;
}

.mobile-time {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-countdown-digit, #ffffff);
  line-height: 1;
}

.mobile-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-countdown-label, rgba(255, 255, 255, 0.92));
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
  margin-top: 2px;
}


/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-header {
    padding: 0px 12px;
    min-height: 56px;
  }
  
  .mobile-lottery-name {
    font-size: 14px;
  }
  
  .mobile-lottery-country {
    font-size: 11px;
  }
  
  .mobile-flag-container {
    width: 28px;
    height: 28px;
  }
  
  .mobile-time {
    font-size: 16px;
  }
  
  .mobile-label {
    font-size: 9px;
  }
  
  .mobile-countdown {
    gap: 4px;
  }
  
  .mobile-time-block {
    min-width: 24px;
  }
  
  .mobile-countdown-section {
    margin: 0 6px;
  }
}

</style>

<script lang="ts">
export default {
  name: "MobileHeader",
};
</script>
